import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Star, Quote, BadgeCheck, Briefcase, TrendingUp, GraduationCap, Play, Pause } from 'lucide-react';
import ScrollReveal from '../effects/ScrollReveal';

const testimonials = [
  {
    name: 'U.VINEETHA',
    role: 'AURUS',
    course: 'Cybersecurity - SOC',
    text: "XceedEdge didn't just teach me cybersecurity - they made sure I got placed. The mentor support was incredible, and the real-time projects gave me confidence in interviews.",
    rating: 5,
    salary: '₹4 LPA',
    hike: '+62%',
    image: 'https://xceededge.com/data/images/U.VINEETHA.jpeg',
    company: 'AURUS',
  }, {
    name: 'T. NEERAJA',
    role: 'Cognizant',
    course: 'Cybersecurity - SOC',
    text: "XceedEdge didn't just teach me cybersecurity - they made sure I got placed. The mentor support was incredible, and the real-time projects gave me confidence in interviews.",
    rating: 5,
    salary: '₹4 LPA',
    hike: '+62%',
    image: 'https://xceededge.com/data/images/T.%20NEERAJA.jpeg',
    company: 'Cognizant',
  },
  
  {
    name: 'Y. Dharmendra',
    role: 'AI Engineer at Tech Table Digital solutions',
    course: 'AI / ML Program',
    text: "The Gen AI module was a game-changer. Praveen sir's mentorship helped me build a portfolio that stood out. Got 3 offers within 2 months!",
    rating: 5,
    salary: '₹5 LPA',
    hike: '+85%',
    image: 'https://xceededge.com/data/images/Y.Dharmendra.jpeg',
    company: 'Tech Table Digital solutions',
  },{
    name: 'G. Sai Madhav',
    role: 'Cognizant',
    course: 'Full Stack Java',
    text: 'Coming from a non-tech background, I was nervous. But the structured roadmap and daily follow-ups kept me on track. Placed within 4 months of completion!',
    rating: 5,
    salary: '₹3.5 LPA',
    hike: '+55%',
    image: 'https://xceededge.com/data/images/G.Sai-Madhav.jpeg',
    company: ' Cognizant',
  },
  {
    name: 'SHAIK MUHAMMAD IRFAN',
    role: 'Lumiq',
    course: 'ServiceNow',
    text: "Pradeep sir's real-world approach made all the difference. The career manager helped me negotiate a 65% salary hike. Best investment I've made.",
    rating: 5,
    salary: '₹6 LPA',
    hike: '+65%',
    image: 'https://xceededge.com/data/images/SHAIK-MUHAMMAD-IRFAN.jpeg',
    company: 'Lumiq',
  },
  // {
  //   name: 'Sneha Patil',
  //   role: 'Security Analyst at HCL',
  //   course: 'VAPT Program',
  //   text: 'After a 3-year career gap, I thought getting back into tech would be impossible. XceedEdge proved me wrong. Their career gap support is genuinely real.',
  //   rating: 5,
  //   salary: '₹5.8 LPA',
  //   hike: '+58%',
  //   image: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=150&q=80',
  //   company: 'HCL',
  // },
];

export default function StudentResults() {
  const [current, setCurrent] = useState(0);
  const [dir, setDir] = useState(1);
  const [autoplay, setAutoplay] = useState(true);

  const next = () => { setDir(1); setCurrent((current + 1) % testimonials.length); };
  const prev = () => { setDir(-1); setCurrent((current - 1 + testimonials.length) % testimonials.length); };

  useEffect(() => {
    if (!autoplay) return;
    const t = setInterval(next, 5000);
    return () => clearInterval(t);
  }, [autoplay, current]);

  const t = testimonials[current];

  return (
    <section className="py-24 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-muted/25 via-transparent to-transparent pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative">
        <ScrollReveal variant="fadeUp" className="text-center mb-16">
          <span className="inline-block text-primary text-sm font-bold tracking-widest uppercase mb-3 px-4 py-1.5 rounded-full glass-yellow">Success Stories</span>
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold mt-4">
            Student <span className="text-primary relative">
              Results
              <motion.div initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }} viewport={{ once: true }} transition={{ delay: 0.3, duration: 0.6 }}
                className="absolute -bottom-1.5 left-0 right-0 h-0.5 bg-primary rounded-full origin-left" />
            </span>
          </h2>
          <p className="text-muted-foreground mt-4 max-w-lg mx-auto text-lg font-body">
            Real students. Real placements. Real salary transformations.
          </p>
        </ScrollReveal>

        {/* Quick stats row */}
        <div className="flex flex-wrap justify-center gap-4 mb-14">
          {[
            { icon: GraduationCap, label: '2K+', sub: 'Students' },
            { icon: Briefcase, label: '94%', sub: 'Placed' },
            { icon: TrendingUp, label: '65%', sub: 'Avg Hike' },
          ].map((s, i) => {
            const StatIcon = s.icon;
            return (
              <ScrollReveal key={s.label} variant="fadeUp" delay={i * 0.1}>
                <motion.div
                  whileHover={{ y: -4, scale: 1.05 }}
                  className="glass rounded-2xl border border-white/80 px-6 py-4 flex items-center gap-3 shadow-sm"
                >
                  <div className="w-10 h-10 rounded-xl bg-primary/15 flex items-center justify-center">
                    <StatIcon className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-heading font-bold text-xl leading-none text-primary">{s.label}</p>
                    <p className="text-xs text-muted-foreground mt-0.5 font-body">{s.sub}</p>
                  </div>
                </motion.div>
              </ScrollReveal>
            );
          })}
        </div>

        {/* Main testimonial layout */}
        <div className="grid lg:grid-cols-5 gap-8 items-start">
          {/* Avatar strip */}
          <div className="hidden lg:flex flex-col gap-3">
            {testimonials.map((t2, i) => (
              <motion.button
                key={t2.name}
                onClick={() => { setDir(i > current ? 1 : -1); setCurrent(i); }}
                whileHover={{ scale: 1.05, x: 4 }}
                className={`flex items-center gap-3 p-3 rounded-2xl border transition-all duration-300 text-left ${
                  current === i ? 'glass border-primary/40 shadow-md' : 'border-transparent hover:bg-white/50'
                }`}
              >
                <img src={t2.image} alt={t2.name}
                  className={`w-10 h-10 rounded-full object-cover border-2 transition-all ${current === i ? 'border-primary' : 'border-white/60'}`} />
                <div className="min-w-0">
                  <p className={`font-heading font-bold text-xs truncate ${current === i ? 'text-primary' : 'text-foreground/70'}`}>{t2.name}</p>
                  <p className="text-[10px] text-muted-foreground truncate font-body">{t2.company}</p>
                </div>
              </motion.button>
            ))}
          </div>

          {/* Testimonial card */}
          <div className="lg:col-span-3" style={{ perspective: 1200 }}>
            <AnimatePresence mode="wait" custom={dir}>
              <motion.div
                key={current}
                custom={dir}
                initial={{ opacity: 0, x: dir > 0 ? 120 : -120, rotateY: dir > 0 ? 8 : -8 }}
                animate={{ opacity: 1, x: 0, rotateY: 0 }}
                exit={{ opacity: 0, x: dir > 0 ? -120 : 120, rotateY: dir > 0 ? -8 : 8 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                className="glass rounded-3xl p-8 relative overflow-hidden shadow-2xl border border-white/80"
              >
                <div className="absolute -top-10 -right-10 w-40 h-40 bg-primary/6 rounded-full blur-3xl" />
                <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-primary/4 rounded-full blur-2xl" />

                <div className="flex items-start justify-between mb-5">
                  <Quote className="w-12 h-12 text-primary/15" />
                  <div className="glass-yellow rounded-xl px-4 py-2 text-center">
                    <p className="font-heading font-bold text-base text-foreground">{t.salary}</p>
                    <p className="text-[10px] text-muted-foreground">Package</p>
                  </div>
                </div>

                <p className="text-lg leading-relaxed text-foreground/80 italic font-medium font-body relative">
                  "{t.text}"
                </p>

                <div className="flex items-center gap-1.5 mt-5">
                  {Array.from({ length: t.rating }).map((_, i) => (
                    <motion.div key={i} initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: i * 0.06, type: 'spring', stiffness: 400 }}>
                      <Star className="w-4 h-4 fill-primary text-primary" />
                    </motion.div>
                  ))}
                  <span className="text-xs text-muted-foreground ml-1 font-semibold">5.0</span>
                </div>

                <div className="flex items-center justify-between mt-6 pt-6 border-t border-black/5">
                  <div className="flex items-center gap-3.5">
                    <div className="relative">
                      <img src={t.image} alt={t.name}
                        className="w-14 h-14 rounded-full object-cover border-2 border-primary/30 shadow-md" />
                      <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-primary flex items-center justify-center border-2 border-white">
                        <BadgeCheck className="w-3 h-3 text-primary-foreground" />
                      </div>
                    </div>
                    <div>
                      <p className="font-heading font-bold text-sm">{t.name}</p>
                      <p className="text-xs text-muted-foreground font-body">{t.role}</p>
                      <p className="text-xs text-primary font-semibold font-body">{t.course}</p>
                    </div>
                  </div>
                  <div className="glass rounded-xl px-3 py-2 text-center border border-primary/20">
                    <p className="font-heading font-bold text-sm text-primary">{t.hike}</p>
                    <p className="text-[10px] text-muted-foreground">Salary Hike</p>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Controls */}
            <div className="flex items-center justify-center gap-3 mt-6">
              <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }} onClick={prev}
                className="w-10 h-10 rounded-full glass border border-white/80 flex items-center justify-center hover:bg-primary/10 transition-colors shadow-sm">
                <ChevronLeft className="w-4 h-4" />
              </motion.button>

              {testimonials.map((_, i) => (
                <motion.button key={i} onClick={() => { setDir(i > current ? 1 : -1); setCurrent(i); }}
                  animate={{ width: i === current ? 28 : 8, backgroundColor: i === current ? 'hsl(47, 99%, 50%)' : 'hsl(0, 0%, 85%)' }}
                  className="h-2 rounded-full" />
              ))}

              <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }} onClick={next}
                className="w-10 h-10 rounded-full glass border border-white/80 flex items-center justify-center hover:bg-primary/10 transition-colors shadow-sm">
                <ChevronRight className="w-4 h-4" />
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => setAutoplay(!autoplay)}
                className="w-10 h-10 rounded-full glass border border-white/80 flex items-center justify-center hover:bg-primary/10 transition-colors shadow-sm ml-1"
              >
                {autoplay ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5 ml-0.5" />}
              </motion.button>
            </div>
          </div>

          {/* Side stats */}
          <div className="hidden lg:flex flex-col gap-4">
            {[
              { label: 'Avg. Time to Placement', value: '4 months' },
              { label: 'Avg. Salary Package', value: '₹6.5 LPA' },
              { label: 'Companies Hiring', value: '25+' },
              { label: 'Student Satisfaction', value: '97%' },
            ].map((s, i) => (
              <ScrollReveal key={s.label} variant="fadeRight" delay={i * 0.1}>
                <motion.div
                  whileHover={{ x: -4 }}
                  className="glass rounded-xl border border-white/80 p-4 shadow-sm"
                >
                  <p className="font-heading font-bold text-xl text-primary">{s.value}</p>
                  <p className="text-xs text-muted-foreground mt-0.5 font-body">{s.label}</p>
                </motion.div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}